import { Presentation,
  Building2, Rocket, CalendarDays, BookOpen, Target, Lightbulb, FileText,
  AlertTriangle, Shield, Users, ClipboardList, Sparkles, LayoutDashboard,
  GraduationCap, LifeBuoy, Mail, Link2, History, Image, Bot, FolderClock, CalendarClock,
  type LucideIcon,
} from 'lucide-react';

export type NavAudience = 'staff' | 'viewer' | 'founder';

export interface NavDestination {
  /** Route path. */
  to: string;
  /** Short label shown in nav and search results. */
  label: string;
  /** Plain-language explanation of what lives there. */
  description: string;
  icon: LucideIcon;
  /** Grouping used by the command palette and the "More" menu. */
  group: string;
  /** Who should see this destination. */
  audiences: NavAudience[];
  /** Shown in the top bar (rather than only in search / More). */
  primary?: boolean;
  /** Extra words people might search for. */
  keywords?: string[];
  /** Bullet points describing what the page actually shows, used for search previews. */
  preview?: string[];
}

export const NAV_DESTINATIONS: NavDestination[] = [
  // --- Shared home -------------------------------------------------------
  {
    to: '/dashboard',
    label: 'Home',
    description: 'Your starting point with shortcuts to everything you can access',
    icon: LayoutDashboard,
    group: 'Overview',
    audiences: ['staff', 'viewer', 'founder'],
    primary: true,
    keywords: ['dashboard', 'start', 'overview', 'home page', 'main', 'landing', 'shortcuts', 'tiles'],
    preview: [
      'Role-aware tiles for every area you can open',
      'Quick links to your most-used pages',
      'A summary of what needs attention next',
    ],
  },
  {
    to: '/admin/startup-library',
    label: 'MavenWERX Startup Library',
    description: 'Every startup in the accelerator',
    icon: Rocket,
    group: 'Overview',
    audiences: ['staff'],
    primary: true,
    keywords: ['startups', 'library', 'companies'],
    preview: ['Searchable list of every startup'],
  },
  {
    to: '/admin/survey-responses',
    label: 'Survey Responses',
    description: 'End-of-program feedback from founders',
    icon: FileText,
    group: 'Overview',
    audiences: ['staff'],
    primary: true,
    keywords: ['survey', 'feedback', 'responses'],
    preview: ['Every founder feedback survey, newest first', 'Spreadsheet download'],
  },
  {
    to: '/admin/field-notes',
    label: 'Field Notes',
    description: 'Topic, notes and action items',
    icon: FileText,
    group: 'Overview',
    audiences: ['staff'],
    primary: true,
    keywords: ['notes', 'field notes', 'action items', 'topic'],
    preview: ['Topic, notes and action item form', 'Saved notes listed newest first'],
  },
  {
    to: '/admin/mentors',
    label: 'Mentors',
    description: 'Coming soon',
    icon: Users,
    group: 'Overview',
    audiences: ['staff'],
    primary: true,
    keywords: ['mentors'],
    preview: ['Coming soon'],
  },

  // --- Founder -----------------------------------------------------------
  {
    to: '/accelerator',
    label: 'My Accelerator',
    description: 'Your sprints, submissions and progress',
    icon: Rocket,
    group: 'Accelerator',
    audiences: ['founder'],
    primary: true,
    keywords: ['sprint', 'week', 'weeks', 'module', 'modules', 'progress', 'curriculum', 'chalk', 'submission', 'homework', 'assignment'],
    preview: [
      'Your current sprint with its questions and uploads',
      'Locked and completed sprints across all three chalks',
      'Generate with AI and draft download tools',
    ],
  },
  {
    to: '/accelerator/phase-0',
    label: 'Phase 0',
    description: 'Onboarding checklist to complete before Sprint 1',
    icon: ClipboardList,
    group: 'Accelerator',
    audiences: ['founder', 'staff'],
    primary: true,
    keywords: ['onboarding', 'checklist', 'getting ready', 'phase zero', 'sprint 0', 'week 0', 'kickoff', 'setup', 'prep'],
    preview: [
      'The pre-program milestone checklist',
      'Which items you have completed and what is left',
      'Links to the forms and documents each step needs',
    ],
  },
  {
    to: '/accelerator/pitch-deck',
    label: 'Pitch Deck Builder',
    description: 'Build an investor-ready pitch deck section by section',
    icon: Presentation,
    group: 'Accelerator',
    audiences: ['founder'],
    primary: true,
    keywords: ['pitch', 'deck', 'investor', 'slides', 'presentation'],
    preview: ['14 guided pitch deck sections', 'Progress toward a pitch-ready deck', 'Feedback from your coach'],
  },
  {
    to: '/accelerator/moneyball',
    label: 'Moneyball',
    description: 'Funding and contract opportunities matched to your company',
    icon: Target,
    group: 'Accelerator',
    audiences: ['founder'],
    primary: true,
    keywords: ['opportunities', 'solicitations', 'sbir', 'sttr', 'grants', 'funding', 'contracts', 'matches', 'rfp', 'baa', 'money'],
    preview: [
      'Opportunities matched to your scout card',
      'Match scores with the reasons behind each one',
      'Deadlines, agencies and links to apply',
    ],
  },
  {
    to: '/accelerator/outline',
    label: 'Accelerator Outline',
    description: 'Every chalk and sprint in the program, start to finish',
    icon: BookOpen,
    group: 'Accelerator',
    audiences: ['founder', 'staff', 'viewer'],
    primary: true,
    keywords: ['outline', 'curriculum', 'program', 'modules', 'agenda', 'syllabus', 'what is covered', 'schedule'],
    preview: [
      'All sixteen sprints grouped by chalk',
      'What each sprint covers and its estimated hours',
      'A direct link to open any sprint',
    ],
  },
  {
    to: '/accelerator/sop',
    label: 'Program SOP',
    description: 'How the accelerator runs, week by week',
    icon: BookOpen,
    group: 'Accelerator',
    audiences: ['founder', 'staff', 'viewer'],
    keywords: ['standard operating procedure', 'handbook', 'rules', 'process', 'policy', 'manual', 'how it works'],
    preview: [
      'The full standard operating procedure',
      'Expectations, cadence and deliverables per sprint',
      'Who to contact for each type of question',
    ],
  },
  {
    to: '/my-application',
    label: 'My Company Profile',
    description: 'Your scout card details and teammates',
    icon: Building2,
    group: 'Accelerator',
    audiences: ['founder'],
    keywords: ['scout card', 'application', 'company', 'teammate', 'team', 'profile', 'logo', 'bio', 'my info', 'edit company'],
    preview: [
      'Your editable scout card fields and company logo',
      'Teammates you have invited (limit of two)',
      'Contact details used across the platform',
    ],
  },
  {
    to: '/getting-started',
    label: 'Getting Started',
    description: 'Step-by-step guide for new founders',
    icon: LifeBuoy,
    group: 'Accelerator',
    audiences: ['founder'],
    keywords: ['help', 'guide', 'new', 'onboarding', 'tutorial', 'how do i', 'first steps', 'support', 'stuck'],
    preview: [
      'A seven-step orientation to the platform',
      'Where to find sprints, uploads and your profile',
      'How to reach The Scout assistant for help',
    ],
  },
  {
    to: '/accelerator/graduation',
    label: 'Graduation',
    description: 'Your completion certificate once all sprints are done',
    icon: GraduationCap,
    group: 'Accelerator',
    audiences: ['founder'],
    keywords: ['certificate', 'complete', 'finish', 'done', 'graduate', 'diploma', 'print', 'award'],
    preview: [
      'Your congratulations screen once all sprints are complete',
      'A print-ready completion certificate',
      'A recap of what you finished in the program',
    ],
  },
  // --- Staff core --------------------------------------------------------
  {
    to: '/admin',
    label: 'Scout Cards',
    description: 'Browse and manage every company profile',
    icon: Building2,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    primary: true,
    keywords: ['companies', 'company', 'startups', 'cards', 'portfolio', 'profiles', 'roster', 'directory', 'businesses'],
    preview: [
      'The full grid of company scout cards',
      'Cohort badges, filters and drag-to-reorder',
      'Open any card for details, files and audit history',
    ],
  },
  {
    to: '/admin/accelerator',
    label: 'Accelerator',
    description: 'Cohorts, startups, sprint progress and modules',
    icon: Rocket,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    primary: true,
    keywords: ['cohort', 'startups', 'sprints', 'weeks', 'progress', 'unlock', 'current week', 'ta hours', 'submissions'],
    preview: [
      'Every enrolled startup with its current sprint',
      'Week unlock overrides and current-week editing',
      'Submission review and TA hour tracking',
    ],
  },
  {
    to: '/admin/events',
    label: 'Events & CRM',
    description: 'Events, contacts and engagement tracking',
    icon: CalendarDays,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    primary: true,
    keywords: ['contacts', 'attendance', 'kpi', 'crm', 'outreach', 'engagements', 'reach', 'calendar', 'meetings'],
    preview: [
      'Event records with attendance and outcomes',
      'Contact list with CSV import and export',
      'Reach and engagement KPI rollups',
    ],
  },
  {
    to: '/admin/ip-library/universities',
    label: 'IP Library',
    description: 'University intellectual property records',
    icon: BookOpen,
    group: 'Programs',
    audiences: ['staff'],
    keywords: ['patents', 'universities', 'technology', 'ip', 'intellectual property', 'licensing', 'tech transfer', 'research'],
    primary: true,
    preview: [
      'University IP records as browsable cards',
      'Filters by university and technology area',
      'Contact details for licensing conversations',
    ],
  },
  {
    to: '/admin/pitch-decks',
    label: 'Pitch Deck Review',
    description: 'Review founder pitch decks and leave coaching feedback',
    icon: Presentation,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    keywords: ['pitch', 'deck', 'coaching', 'feedback', 'investor'],
    preview: ['Every startup pitch deck progress', 'Sections flagged for coaching', 'Comment and approve sections'],
  },
  {
    to: '/admin/moneyball',
    label: 'Moneyball',
    description: 'Opportunities and startup matches',
    icon: Target,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    keywords: ['opportunities', 'solicitations', 'matches', 'sbir', 'sttr', 'funding', 'contracts', 'pipeline'],
    preview: [
      'The opportunity catalog you maintain',
      'Match results per startup with scores',
      'Controls to re-run matching',
    ],
  },
  {
    to: '/louisiana-technology-office',
    label: 'LTTO',
    description: 'Louisiana Technology Transfer Office resources',
    icon: Lightbulb,
    group: 'Programs',
    audiences: ['staff', 'viewer'],
    keywords: ['louisiana', 'technology transfer', 'innovation form', 'ltto', 'la tech office', 'led', 'state'],
    preview: [
      'LTTO reference material and partner resources',
      'The Innovation Interest Form link',
      'Guidance for working with Louisiana programs',
    ],
  },
  {
    to: '/admin/accelerator/modules',
    label: 'Module Editor',
    description: 'Edit sprint content, prompts and required uploads',
    icon: FileText,
    group: 'Content',
    audiences: ['staff'],
    keywords: ['curriculum', 'weeks', 'questions', 'modules', 'prompts', 'content', 'edit sprint', 'videos', 'requirements'],
    preview: [
      'All sixteen modules with their titles and chalks',
      'Question prompts, upload requirements and videos',
      'Estimated hours per module',
    ],
  },
  {
    to: '/admin/copy',
    label: 'Site Copy',
    description: 'Edit landing-page and public text',
    icon: FileText,
    group: 'Content',
    audiences: ['staff'],
    keywords: ['cms', 'text', 'wording', 'copy', 'content', 'landing page', 'headline', 'marketing'],
    preview: [
      'Editable text blocks used on public pages',
      'Live keys from the site copy table',
      'Changes publish without a code deploy',
    ],
  },
  {
    to: '/admin/guide',
    label: 'Admin Guide',
    description: 'How to run the platform day to day',
    icon: BookOpen,
    group: 'Content',
    audiences: ['staff', 'viewer'],
    keywords: ['help', 'documentation', 'guide', 'manual', 'how to', 'training', 'runbook'],
    preview: [
      'Task-by-task instructions for staff',
      'Common workflows like invites and unlocks',
      'Where to look when something breaks',
    ],
  },

  // --- People & invites --------------------------------------------------
  {
    to: '/admin/pending-teammate-invites',
    label: 'Pending Teammate Invites',
    description: 'Teammates invited but not yet signed in',
    icon: Users,
    group: 'People & invites',
    audiences: ['staff'],
    keywords: ['invite', 'invitation', 'teammate', 'resend', 'pending', 'not accepted', 'waiting', 'csv export'],
    preview: [
      'Every invite that has not been accepted yet',
      'Resend controls and invite age',
      'Server-side CSV export of the current list',
    ],
  },
  {
    to: '/admin/teammate-linkage',
    label: 'Teammate Linkage',
    description: 'Which teammates are connected to which company',
    icon: Users,
    group: 'People & invites',
    audiences: ['staff'],
    keywords: ['teammate', 'link', 'accounts', 'linkage', 'mapping', 'company', 'attach', 'orphan'],
    preview: [
      'Teammate accounts mapped to their startup',
      'Unlinked or orphaned accounts to fix',
      'Tools to repoint a teammate to the right company',
    ],
  },
  {
    to: '/admin/email-associations',
    label: 'Email Associations',
    description: 'Emails mapped to accounts and companies',
    icon: Mail,
    group: 'People & invites',
    audiences: ['staff'],
    keywords: ['email', 'accounts', 'association', 'address', 'login', 'who owns', 'mapping'],
    preview: [
      'Each email address and the account it belongs to',
      'The company or scout card it resolves to',
      'A fast way to check sign-in problems',
    ],
  },
  {
    to: '/admin/email-mismatches',
    label: 'Email Mismatches',
    description: 'Sign-ups whose email differs from the invite',
    icon: AlertTriangle,
    group: 'People & invites',
    audiences: ['staff'],
    keywords: ['mismatch', 'reconcile', 'invite', 'wrong email', 'typo', 'cannot log in', 'access problem'],
    preview: [
      'Accounts created with an email that does not match the invite',
      'Side-by-side invite versus sign-up address',
      'Reconciliation actions to restore access',
    ],
  },

  // --- Monitoring --------------------------------------------------------
  {
    to: '/admin/error-reports',
    label: 'Error Reports',
    description: 'Issues founders reported through The Scout',
    icon: AlertTriangle,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['support', 'bugs', 'assistant', 'chatbot', 'the scout', 'tickets', 'complaints', 'errors'],
    preview: [
      'Reports The Scout escalated to admins',
      'The founder, page and error context for each',
      'Status so you can track what is fixed',
    ],
  },
  {
    to: '/admin/ai-attempts',
    label: 'AI Generation Attempts',
    description: 'Every "Generate with AI" run and why it failed',
    icon: Bot,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['ai', 'draft', 'failures', 'generate', 'gemini', 'llm', 'request id', 'retry', 'module draft'],
    preview: [
      'Each generation attempt with week and module id',
      'Status, backend error text and request ID',
      'A retry button to re-run a failed generation',
    ],
  },
  {
    to: '/admin/week-unlocks',
    label: 'Week Unlocks',
    description: 'Which sprints are open per Scout Card and when they opened',
    icon: CalendarClock,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['weeks', 'unlock', 'sprints', 'progress', 'current week', 'gating', 'timeline', 'access'],
    preview: [
      'A week-by-week unlock grid for every company',
      'When each week opened and who enabled it',
      'CSV export of the filtered unlock history',
    ],
  },
  {
    to: '/admin/data-consistency',
    label: 'Data Consistency Checks',
    description: 'Mismatches between submissions, progress records and completion %',
    icon: CalendarClock,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['consistency', 'mismatch', 'audit', 'progress', 'completion', 'sync', 'data quality', 'reconcile'],
    preview: [
      'Every mismatch grouped by company and week',
      'Severity, filters and plain-English explanations',
      'CSV export of the filtered findings',
    ],
  },
  {
    to: '/admin/business-continuity',
    label: 'Business Continuity Assessment',
    description: 'Continuity assessment scores, readiness levels and gaps for every company',
    icon: ClipboardList,
    group: 'Monitoring',
    audiences: ['staff', 'viewer'],
    keywords: ['business continuity', 'continuity', 'disaster', 'recovery', 'resilience', 'risk', 'assessment'],
    preview: [
      'Readiness score and level per company',
      'Priority gap areas by section',
      'Founder notes for the review team',
    ],
  },
  {
    to: '/admin/sbop-responses',
    label: 'SBOP Responses',
    description: 'Every SBOP demographic form submission, searchable with CSV export',
    icon: ClipboardList,
    group: 'Monitoring',
    audiences: ['staff'],
    primary: true,
    keywords: ['sbop', 'demographic', 'led', 'small business', 'responses', 'submissions', 'form', 'certifications'],
    preview: [
      'Each submitted SBOP form with contact and business details',
      'Search by business, contact, parish or industry',
      'CSV export of the filtered list',
    ],
  },
  {
    to: '/admin/outreach-log',
    label: 'Outreach Log',
    description: 'Which companies reached out to BBC or other partners, and what happened',
    icon: ClipboardList,
    group: 'Monitoring',
    audiences: ['staff', 'viewer'],
    keywords: ['outreach', 'bbc', 'partner', 'contacted', 'intro', 'meeting', 'follow up', 'log'],
    preview: [
      'Every logged outreach by company and partner',
      'Method, status, contact person and notes',
      'CSV export of the filtered entries',
    ],
  },
  {
    to: '/admin/phase0-documents',
    label: 'Phase 0 Documents',
    description: 'Which companies have submitted and approved Phase 0 paperwork',
    icon: ClipboardList,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['phase zero', 'documents', 'paperwork', 'uploads', 'agreement', 'w-9', 'nda', 'review', 'approve', 'complete'],
    preview: [
      'Every company with its Phase 0 completion status',
      'Uploaded documents and written responses per item',
      'Approve or request a revision with a reviewer note',
    ],
  },
  {
    to: '/admin/phase0-milestones',
    label: 'Phase 0 Milestones',
    description: 'Who completed which onboarding step, and when',
    icon: ClipboardList,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['onboarding', 'checklist', 'audit', 'phase zero', 'milestones', 'completion', 'progress', 'export'],
    preview: [
      'Milestone completion events per founder',
      'Search, filters and date ranges',
      'Export of the filtered results',
    ],
  },
  {
    to: '/admin/system-health',
    label: 'System Health',
    description: 'Nightly watchdog: auto-repairs and alerts',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: [],
    preview: ['Nightly watchdog: auto-repairs and alerts'],
  },
  {
    to: '/admin/security/signin-attempts',
    label: 'Sign-in Attempts',
    description: 'Recent successful and failed sign-ins',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: [],
    preview: ['Recent successful and failed sign-ins'],
  },
  {
    to: '/admin/audit-log',
    label: 'Audit Log',
    description: 'Full history of admin actions',
    icon: History,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['history', 'changes', 'who did what', 'audit', 'trail', 'log', 'activity', 'before after'],
    preview: [
      'Every recorded admin action in order',
      'Expandable before and after values',
      'Shareable filtered links via the URL',
    ],
  },
  {
    to: '/admin/file-activity',
    label: 'File Activity',
    description: 'Uploads, replacements, downloads and deletions',
    icon: FolderClock,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['files', 'uploads', 'downloads', 'deleted', 'documents', 'attachments', 'activity', 'log'],
    preview: [
      'Who uploaded, downloaded or deleted each file',
      'Company, week and file size context',
      'Search, action filters and CSV export',
    ],
  },
  {

    to: '/admin/logo-audit',
    label: 'Logo Audit',
    description: 'Company logo uploads and repoints',
    icon: Image,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['logo', 'images', 'uploads', 'branding', 'broken image', 'intake', 'repoint', 'picture'],
    preview: [
      'Logo repoint and intake upload events',
      'Before and after logo_url values',
      'Timestamps for tracing broken images',
    ],
  },
  {
    to: '/admin/link-audit',
    label: 'Link Audit',
    description: 'Broken or unreachable links across the platform',
    icon: Link2,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['links', 'broken', 'urls', 'websites', '404', 'dead link', 'unreachable'],
    preview: [
      'Links checked across scout cards and content',
      'Which ones failed and the response returned',
      'Where each link lives so you can fix it',
    ],
  },
  {
    to: '/admin/security',
    label: 'Security',
    description: 'Security findings, scans and remediation',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['vulnerabilities', 'scan', 'findings', 'security', 'rls', 'risk', 'remediation', 'compare scans'],
    preview: [
      'Open findings with severity and status',
      'Nightly scan history with base versus compare',
      'Remediation notes and ignore controls',
    ],
  },
  {
    to: '/admin/security/latest',
    label: 'Security Scan Results',
    description: 'Latest scan grouped by critical, error and warning',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['scan results', 'critical', 'error', 'warning', 'latest scan', 'security check'],
    preview: [
      'Counts of critical, error and warning findings',
      'Every open finding from the most recent scan',
      'When the last scan ran and what it checked',
    ],
  },
  {
    to: '/admin/security/scans',
    label: 'Scheduled Security Scans',
    description: 'Daily automated security sweep history with full results',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['scheduled', 'recurring', 'sweep', 'history', 'security scan', 'automated', 'daily'],
    preview: [
      'Every scheduled and manual sweep with pass or fail status',
      'Findings, endpoint access checks and protection posture per run',
      'Full technical results kept for review',
    ],
  },
  {
    to: '/admin/security/posture',
    label: 'Security Audit Summary',
    description: 'RLS and function execute posture, with new findings after deployments',
    icon: Shield,
    group: 'Monitoring',
    audiences: ['staff'],
    keywords: ['rls', 'posture', 'policies', 'grants', 'execute', 'security definer', 'audit', 'snapshot'],
    preview: [
      'Every table: row protection, rule count and role privileges',
      'Every function: elevated rights and signed-out callability',
      'New findings flagged against the previous snapshot',
    ],
  },

  {
    to: '/admin/email-list',
    label: 'Ecosystem Email List',
    description: 'Every contact email across the MavenWERX innovation ecosystem',
    icon: Mail,
    group: 'People & invites',
    audiences: ['staff'],
    primary: true,
    keywords: ['email list', 'contacts', 'mailing list', 'newsletter', 'ecosystem', 'addresses', 'export emails', 'distribution list'],
    preview: [
      'One de-duplicated list of every contact email we hold',
      'Filter by source: scout cards, teammates, CRM, SBOP, referrals, IP',
      'Copy all addresses or export the filtered list to CSV',
    ],
  },

  // --- Public ------------------------------------------------------------
  {
    to: '/intake',
    label: 'Intake Form',
    description: 'Public application form for new companies',
    icon: Sparkles,
    group: 'Public pages',
    audiences: ['staff'],
    keywords: ['apply', 'application', 'public', 'intake', 'signup form', 'new company', 'submission form'],
    preview: [
      'The three-step public application flow',
      'Logo and document upload steps',
      'Exactly what applicants see before submitting',
    ],
  },
  {
    to: '/',
    label: 'Public Website',
    description: 'The public MavenWERX landing page',
    icon: Building2,
    group: 'Public pages',
    audiences: ['staff', 'viewer', 'founder'],
    keywords: ['home', 'landing', 'marketing', 'public site', 'website', 'front page'],
    preview: [
      'The public-facing MavenWERX landing page',
      'Program overview and calls to action',
      'What visitors see before signing in',
    ],
  },
];

/** Routes that should never show the signed-in app navigation. */
export const PUBLIC_ROUTES = ['/', '/intake', '/login', '/reset-password', '/sbop', '/accelerator/sbop', '/dual-use-accelerator', '/about', '/partners', '/events', '/newsletter', '/shop', '/gsdt-cluster'];

export function destinationsFor(audience: NavAudience | null): NavDestination[] {
  if (!audience) return [];
  return NAV_DESTINATIONS.filter((d) => d.audiences.includes(audience));
}

/** Everything the palette should match a query against, as one searchable string. */
export function searchTextFor(d: NavDestination): string {
  return [d.label, d.description, d.group, d.to, ...(d.keywords ?? []), ...(d.preview ?? [])].join(' ');
}

/** Human-friendly label for a path segment when no destination matches. */
export function humanizeSegment(segment: string): string {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment)) return 'Details';
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Build breadcrumb trail entries for a pathname. */
export function breadcrumbsFor(pathname: string): { label: string; to: string }[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: { label: string; to: string }[] = [];
  let acc = '';
  for (const segment of segments) {
    acc += `/${segment}`;
    const match = NAV_DESTINATIONS.find((d) => d.to === acc);
    let label = match ? match.label : humanizeSegment(segment);
    // "/admin" is the Scout Cards page, but as a parent of other admin pages it reads as "Admin".
    if (acc === '/admin' && segments.length > 1) label = 'Admin';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment)) label = 'Details';
    crumbs.push({ label, to: acc });
  }
  return crumbs;
}
